import * as THREE from 'three';

const NODE_CORE_RADIUS = 0.155;
/** Extra click/hover slop when nothing is in the way. */
const HIT_PAD_PX = 40;
const HIT_PAD_MUL = 2.6;
const HIT_GAP_PX = 3;

const _ndc = new THREE.Vector3();
const _edge = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _right = new THREE.Vector3();
const _nodes = [];

function collectHitNodes(camera, meshByTaskId, width, height) {
  _nodes.length = 0;
  camera.getWorldDirection(_fwd);
  _right.crossVectors(_fwd, camera.up);
  if (_right.lengthSq() < 1e-8) return _nodes;
  _right.normalize();
  meshByTaskId.forEach((mesh) => {
    const ud = mesh.userData;
    if (!mesh.visible || ud.exiting || (ud.visT ?? 1) < 0.05) return;
    _ndc.copy(mesh.position).project(camera);
    if (_ndc.z < -1 || _ndc.z > 1) return;
    const x = (_ndc.x * 0.5 + 0.5) * width;
    const y = (-_ndc.y * 0.5 + 0.5) * height;
    const worldR = NODE_CORE_RADIUS * (ud.layoutScale > 0 ? ud.layoutScale : Math.max(mesh.scale.x, 0.001));
    _edge.copy(mesh.position).addScaledVector(_right, worldR).project(camera);
    const visualR = Math.max(
      5,
      Math.hypot((_edge.x - _ndc.x) * width * 0.5, (_edge.y - _ndc.y) * height * 0.5),
    );
    const desiredPad = Math.max(HIT_PAD_PX, visualR * HIT_PAD_MUL);
    _nodes.push({ mesh, x, y, visualR, desiredPad, hitR: visualR + desiredPad });
  });
  return _nodes;
}

function shrinkHitsToFit() {
  const n = _nodes.length;
  for (let i = 0; i < n; i += 1) {
    const a = _nodes[i];
    for (let j = i + 1; j < n; j += 1) {
      const b = _nodes[j];
      const d = Math.hypot(a.x - b.x, a.y - b.y) - HIT_GAP_PX;
      if (a.hitR + b.hitR <= d) continue;
      const slack = Math.max(0, d - a.visualR - b.visualR);
      const tot = a.desiredPad + b.desiredPad;
      a.hitR = Math.min(a.hitR, a.visualR + slack * (a.desiredPad / tot));
      b.hitR = Math.min(b.hitR, b.visualR + slack * (b.desiredPad / tot));
    }
  }
}

/**
 * Hover pick — O(n), nearest disk. No pairwise shrink (that was n² on every move).
 */
export function pickNearestNode(camera, meshByTaskId, clientX, clientY, rect) {
  const width = rect.width;
  const height = rect.height;
  const px = clientX - rect.left;
  const py = clientY - rect.top;
  camera.getWorldDirection(_fwd);
  _right.crossVectors(_fwd, camera.up);
  if (_right.lengthSq() < 1e-8) return null;
  _right.normalize();
  let best = null;
  let bestD = Infinity;
  meshByTaskId.forEach((mesh) => {
    const ud = mesh.userData;
    if (!mesh.visible || ud.exiting || (ud.visT ?? 1) < 0.05) return;
    _ndc.copy(mesh.position).project(camera);
    if (_ndc.z < -1 || _ndc.z > 1) return;
    const x = (_ndc.x * 0.5 + 0.5) * width;
    const y = (-_ndc.y * 0.5 + 0.5) * height;
    const d = Math.hypot(px - x, py - y);
    const worldR = NODE_CORE_RADIUS * (ud.layoutScale > 0 ? ud.layoutScale : Math.max(mesh.scale.x, 0.001));
    _edge.copy(mesh.position).addScaledVector(_right, worldR).project(camera);
    const visualR = Math.max(
      5,
      Math.hypot((_edge.x - _ndc.x) * width * 0.5, (_edge.y - _ndc.y) * height * 0.5),
    );
    if (d <= visualR + HIT_PAD_PX && d < bestD) {
      best = mesh;
      bestD = d;
    }
  });
  return best;
}

/**
 * Nearest node whose non-overlapping padded disk covers the pointer.
 * Isolated nodes get a large slop; close neighbors split the gap.
 */
export function pickPaddedNode(camera, meshByTaskId, clientX, clientY, rect) {
  const nodes = collectHitNodes(camera, meshByTaskId, rect.width, rect.height);
  if (!nodes.length) return null;
  shrinkHitsToFit();
  const px = clientX - rect.left;
  const py = clientY - rect.top;
  let best = null;
  let bestD = Infinity;
  for (let i = 0; i < nodes.length; i += 1) {
    const n = nodes[i];
    const d = Math.hypot(px - n.x, py - n.y);
    if (d <= n.hitR && d < bestD) {
      best = n.mesh;
      bestD = d;
    }
  }
  return best;
}
